<template>
  <div class="holidayguru hg-ibe-search-wrapper">
    <HgAmadeusPackageTourSearch
      :default-date-relative="defaultDateRelative"
      :default-region-id="defaultRegionId"
      :default-country-id="defaultCountryId"
      :default-adults="defaultAdults"
      :default-destination-name="defaultDestinationName"
      default-destination-type="Region"
      :default-child-ages="defaultChildAges"
      :is-mobile="isMobile"
      :is-loading-destinations="isLoading"
      :airports="allAirports"
      :destinations="orderedDestinations"
      :ibe-url="searchUrl"
      :ibe-tags="amadeusPackageTourCategories"
      :searched-airports="[]"
      :infoTextSingleRoom="$t('amadeusPackageTourSearch.infoTextSingleRoom')"
      @onUpdateDestinationTerm="updateDestinationTerm"
      @onUpdateSearchParameters="updateSearchParameters"
      @onTriggerSearch="triggerSearch"
    />
  </div>
</template>

<script>
import { addDays, format } from "date-fns"
import allAirportsQuery from "../apollo/queries/airports/allAirports.gql"
import autosuggestAmadeusQuery from "../apollo/queries/amadeus/suggest.gql"
import amadeusPackageTourCategoriesQuery from "../apollo/queries/amadeus/categories.gql"
import amadeusPackageTourSearchUrlQuery from "../apollo/queries/amadeus/searchUrl.gql"

export default {
  props: {
    defaultCountryId: String,
    defaultRegionId: Number,
    defaultDestinationName: {
      type: String,
      default: ""
    },
    defaultAdults: {
      type: Number,
      default: 2
    },
    defaultDateRelative: {
      type: Number,
      default: 0
    },
    defaultChildAges: {
      type: Number,
      default: 6
    },
    defaultAgent: {
      type: String,
      default: ""
    }
  },

  data: () => ({
    isMobile: false,
    isLoading: false,
    destinationTerm: "",
    orderedDestinations: [],
    autosuggestAmadeus: [],
    allAirports: [],
    searchUrl: "",
    destination: null,
    airports: null,
    dateRange: null,
    duration: null,
    travelers: { adults: 2 },
    hasParams: false
  }),

  created() {
    if (this.defaultRegionId) {
      this.destination = {
        id: this.defaultRegionId,
        iffRegionId: this.defaultRegionId,
        name: this.defaultDestinationName,
        type: "Region"
      }
    } else if (this.defaultCountryId) {
      this.destination = {
        id: this.defaultRegionId,
        countryISO3166: this.defaultCountryId,
        name: this.defaultDestinationName,
        type: "Country"
      }
    }
  },

  mounted() {
    window.addEventListener("resize", this.onResize)
    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("resize", this.onResize)
    })
    this.onResize()
  },

  watch: {
    amadeusPackageTourSearchUrl() {
      let url = new URL(this.amadeusPackageTourSearchUrl)

      if (this.defaultAgent) {
        url.searchParams.append("agent", this.defaultAgent)
      }

      this.searchUrl = url.href
    },

    autosuggestAmadeus() {
      if (this.autosuggestAmadeus && this.autosuggestAmadeus.length > 0) {
        // Get relevant types
        const orderTypes = []
        this.autosuggestAmadeus.forEach((elem) => {
          if (!orderTypes.includes(elem.type)) {
            orderTypes.push(elem.type)
          }
        })

        // Order results by types
        const ordered = []
        orderTypes.forEach((element) => {
          ordered.push(
            this.autosuggestAmadeus.filter(function (elem) {
              return elem.type === element
            })
          )
        })

        this.orderedDestinations = [].concat(...ordered)
      }
    }
  },

  methods: {
    onResize() {
      if (!window) return
      const width = window.innerWidth
      this.isMobile = width < 768
    },
    updateDestinationTerm(term) {
      this.destinationTerm = term
    },

    updateSearchParameters(params) {
      const { destination, airports, dateRange, duration, travelers, tags } =
        params

      this.destination = destination
      if (this.destination && this.destination.__typename) {
        delete this.destination.__typename
        delete this.destination.villageName
        delete this.destination.provinceName
        delete this.destination.countryName
        delete this.destination.destinationName
      }

      this.airports = []

      if (airports != null) {
        for (let airport of airports) {
          this.airports.push(airport.iataCode)
        }
      }

      this.dateRange = dateRange
      if (travelers) {
        this.travelers = travelers
      }
      this.duration = duration || ""
      this.tags = (tags && tags.map((t) => t.id)) || []
      this.hasParams = true
    },

    triggerSearch() {
      if (window == null || window.dataLayer == null) {
        return
      }

      let duration = this.duration

      if (duration == "9") {
        duration = "10"
      } else if (duration.includes(",")) {
        let durationDays = duration.split(",")
        duration = `${+durationDays[0] + 1},${+durationDays[1] + 1}`
      }

      window.dataLayer.push({
        event: "IBE_search_used",
        destination:
          this.destination && this.destination.name
            ? this.destination.name
            : "any", // Reiseziel bzw. Hotel
        period: `${format(this.dateRange.start, "EE, dd.MM.yyyy")} - ${format(
          this.dateRange.end,
          "EE, dd.MM.yyyy"
        )}`, // Zeitraum
        travelDuration: duration, // Reisedauer
        departureAirport: this.airports, // Da Mehrfachauswahl möglich: Mehrere Flughäfen am besten in einem Array ["FRA", "MUC"]
        travellers: this.travelers // Reisende
      })
    }
  },

  apollo: {
    allAirports: {
      query: allAirportsQuery,
      prefetch: true,
      variables() {
        return {
          filter: {
            onlyGrouped: true
          }
        }
      }
    },

    autosuggestAmadeus: {
      query: autosuggestAmadeusQuery,
      debounce: 300,
      skip() {
        return this.destinationTerm === ""
      },
      variables() {
        return {
          search: this.destinationTerm
        }
      }
    },

    amadeusPackageTourCategories: {
      query: amadeusPackageTourCategoriesQuery
    },

    amadeusPackageTourSearchUrl: {
      query: amadeusPackageTourSearchUrlQuery,
      skip() {
        return this.hasParams === false
      },
      variables() {
        return {
          input: {
            destination: this.destination,
            startDate: addDays(this.dateRange.start, 1),
            endDate: addDays(this.dateRange.end, 1),
            duration: this.duration,
            airports: this.airports,
            numberOfAdults: this.travelers.adults,
            agesOfChildren: this.travelers.ages,
            categories: this.tags
          }
        }
      }
    }
  }
}
</script>

<style></style>
